import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import {
  AlertTriangle,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Copy,
  MoreHorizontal,
  Plus,
  Settings2,
  Trash2,
  UsersRound,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  MobileBottomSlots,
  MobileTopCenter,
  useMobilePageTitle,
} from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarSlot, SheetRow } from '../components/ui/mobile-action-bar'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import { SegmentedControl } from '../components/ui/tab-nav'
import {
  type Absence,
  type AbsenceType,
  api,
  type ShiftAssignment,
  type ShiftType,
} from '../lib/api'
import { notify } from '../lib/notify'
import { periodTitle } from '../lib/period-title'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/team',
  component: TeamPlanningPage,
})

// ── Date helpers ────────────────────────────────────────────────────────

function startOfWeek(d: Date): Date {
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  const start = new Date(d)
  start.setDate(diff)
  start.setHours(0, 0, 0, 0)
  return start
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d)
  r.setDate(r.getDate() + n)
  return r
}

function formatDateISO(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

function daysInMonth(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
}

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
const WEEKDAY_FROM_JS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
const MONTHS_LONG = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
]

type ViewMode = 'week' | 'month'

const VIEW_OPTIONS: readonly { id: ViewMode; label: string }[] = [
  { id: 'week', label: 'Woche' },
  { id: 'month', label: 'Monat' },
]

const ABSENCE_BG: Record<AbsenceType, string> = {
  vacation: 'rgba(59,130,246,0.12)',
  sick: 'rgba(239,68,68,0.12)',
  training: 'rgba(16,185,129,0.12)',
  other: 'rgba(163,151,140,0.10)',
  overtime_reduction: 'rgba(163,151,140,0.12)',
  maternity: 'rgba(163,151,140,0.12)',
  parental: 'rgba(163,151,140,0.12)',
}

const ABSENCE_ICON: Record<AbsenceType, string> = {
  vacation: '\u{1F3D6}\u{FE0F}',
  sick: '\u{1F912}',
  training: '\u{1F4DA}',
  other: '\u{2026}',
  overtime_reduction: '\u{231B}',
  maternity: '\u{1F37C}',
  parental: '\u{1F37C}',
}

const ABSENCE_LABEL: Record<AbsenceType, string> = {
  vacation: 'Urlaub',
  sick: 'Krank',
  training: 'Fortbildung',
  other: 'Sonstiges',
  overtime_reduction: 'Überstundenabbau',
  maternity: 'Mutterschutz',
  parental: 'Elternzeit',
}

// ── Main component ──────────────────────────────────────────────────────

function TeamPlanningPage() {
  const qc = useQueryClient()
  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })

  const [view, setView] = useState<ViewMode>('week')
  const [cursor, setCursor] = useState(() => startOfWeek(new Date()))
  const [typesOpen, setTypesOpen] = useState(false)

  // Mobile-Shell (<640): ◀ Zeitraum ▶ in der Top-Mitte, Slots Ansicht/
  // Team/Mehr als Sheets (ein State, drei Sheets — bestehende Handler).
  useMobilePageTitle('Teamplanung')
  const [mobileSheet, setMobileSheet] = useState<null | 'view' | 'team' | 'more'>(null)
  const [editCell, setEditCell] = useState<{
    userId: string
    date: string
    userName: string
  } | null>(null)
  const [teamFilter, setTeamFilter] = useState('')

  // ── Drag-and-drop state ──────────────────────────────────────────────
  const [dragShift, setDragShift] = useState<{
    assignmentId: string | null // null when dragging from legend
    shiftTypeId: string
    sourceUserId: string | null // null when dragging from legend
    sourceDate: string | null // null when dragging from legend
    shiftTypeName: string
    shiftTypeShortName: string
    shiftTypeColor: string
  } | null>(null)
  const [ghostPos, setGhostPos] = useState<{ x: number; y: number } | null>(null)
  const [dropTarget, setDropTarget] = useState<{ userId: string; date: string } | null>(null)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dragStartPos = useRef<{ x: number; y: number } | null>(null)
  const isDragging = useRef(false)
  const pointerIdRef = useRef<number | null>(null)

  // Compute the date range based on view
  const dateRange = useMemo(() => {
    if (view === 'week') {
      const weekStart = startOfWeek(cursor)
      return {
        days: Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
        from: formatDateISO(weekStart),
        to: formatDateISO(addDays(weekStart, 6)),
      }
    }
    const monthStart = startOfMonth(cursor)
    const count = daysInMonth(cursor)
    return {
      days: Array.from({ length: count }, (_, i) => addDays(monthStart, i)),
      from: formatDateISO(monthStart),
      to: formatDateISO(addDays(monthStart, count - 1)),
    }
  }, [view, cursor])

  const { from, to, days } = dateRange

  // Data queries
  const { data: weekData, isLoading } = useQuery({
    queryKey: ['shifts', 'week', from, to],
    queryFn: () => api.getShiftWeek(from, to),
    enabled: !!me?.user,
  })

  const { data: typesData } = useQuery({
    queryKey: ['shift-types'],
    queryFn: () => api.listShiftTypes(),
    enabled: !!me?.user,
  })

  const { data: absencesData } = useQuery({
    queryKey: ['absences', 'team', from, to],
    queryFn: () => api.listTeamAbsences({ from, to }),
    enabled: !!me?.user,
  })

  const { data: teamsData } = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.listTeams(),
    enabled: !!me?.user,
  })

  const types = typesData?.types ?? []
  const allUsers = weekData?.users ?? []
  const assignments = weekData?.assignments ?? []
  const absences = absencesData?.absences ?? []
  const teams = teamsData?.teams ?? []
  const isAdmin = me?.user?.role === 'admin'

  // Build lookup maps
  const assignmentMap = useMemo(() => {
    const map: Record<string, ShiftAssignment[]> = {}
    for (const a of assignments) {
      const key = `${a.userId}_${a.date}`
      if (!map[key]) map[key] = []
      map[key].push(a)
    }
    return map
  }, [assignments])

  const absenceMap = useMemo(() => {
    const map: Record<string, Absence[]> = {}
    for (const a of absences) {
      // Abgelehnte/zurückgezogene/stornierte Anträge färben die Planung
      // nicht (und zählen nicht als Konflikt) — nur wirksame + offene.
      if (a.status !== 'approved' && a.status !== 'pending') continue
      // Expand absence range into individual days within view
      const aFrom = new Date(a.fromDate)
      const aTo = new Date(a.toDate)
      const viewFrom = new Date(from)
      const viewTo = new Date(to)
      const clampedFrom = aFrom < viewFrom ? viewFrom : aFrom
      const clampedTo = aTo > viewTo ? viewTo : aTo
      const cur = new Date(clampedFrom)
      while (cur <= clampedTo) {
        const key = `${a.userId}_${formatDateISO(cur)}`
        if (!map[key]) map[key] = []
        map[key].push(a)
        cur.setDate(cur.getDate() + 1)
      }
    }
    return map
  }, [absences, from, to])

  const filteredUsers = useMemo(
    () =>
      teamFilter
        ? allUsers.filter((u) => {
            const team = teams.find((t) => t.id === teamFilter)
            if (!team) return true
            return team.members?.some((m: { userId: string }) => m.userId === u.id)
          })
        : allUsers,
    [teamFilter, allUsers, teams],
  )

  // Summary counts: per day, count employees per shift type
  const summaryByDay = useMemo(() => {
    const result: Record<string, Record<string, number>> = {}
    for (const d of days) {
      const dateStr = formatDateISO(d)
      const counts: Record<string, number> = {}
      for (const user of filteredUsers) {
        const cellAssignments = assignmentMap[`${user.id}_${dateStr}`] ?? []
        for (const a of cellAssignments) {
          const key = a.shiftTypeShortName ?? 'C'
          counts[key] = (counts[key] ?? 0) + 1
        }
      }
      result[dateStr] = counts
    }
    return result
  }, [days, filteredUsers, assignmentMap])

  // Copy previous week
  const copyWeekMut = useMutation({
    mutationFn: async () => {
      const prevFrom = formatDateISO(addDays(new Date(from), -7))
      const prevTo = formatDateISO(addDays(new Date(from), -1))
      const prevData = await api.getShiftWeek(prevFrom, prevTo)
      const bulkAssignments = prevData.assignments
        .filter((a) => a.shiftTypeId)
        .map((a) => ({
          userId: a.userId,
          date: formatDateISO(addDays(new Date(a.date), 7)),
          shiftTypeId: a.shiftTypeId as string,
        }))
      if (bulkAssignments.length === 0) throw new Error('Keine Schichten in der Vorwoche')
      return api.bulkCreateShifts(bulkAssignments)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shifts'] })
      notify.success('Vorwoche kopiert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  // ── Drag mutations ────────────────────────────────────────────────────
  const moveShiftMut = useMutation({
    mutationFn: ({ id, date }: { id: string; date: string }) => api.updateShift(id, { date }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shifts'] })
      notify.success('Schicht verschoben')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const copyShiftMut = useMutation({
    mutationFn: (data: { userId: string; date: string; shiftTypeId: string }) =>
      api.createShift(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shifts'] })
      notify.success('Schicht kopiert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const deleteShiftMut = useMutation({
    mutationFn: (id: string) => api.deleteShift(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shifts'] })
      notify.success('Schicht gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  // Drop-Target Mülleimer: aktiv wenn Pointer beim Drop über Trash-Bereich war
  const [dropTargetTrash, setDropTargetTrash] = useState(false)

  // ── Drag helpers ─────────────────────────────────────────────────────
  const cancelDrag = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
    setDragShift(null)
    setGhostPos(null)
    setDropTarget(null)
    setDropTargetTrash(false)
    // Keep isDragging true briefly so the click event (which fires after pointerup)
    // doesn't open the edit panel on the drop target cell
    setTimeout(() => {
      isDragging.current = false
    }, 50)
    dragStartPos.current = null
    pointerIdRef.current = null
  }, [])

  const handleDrop = useCallback(() => {
    if (!dragShift) {
      cancelDrag()
      return
    }
    // Mülleimer-Drop: nur wenn Assignment-ID existiert (bestehende Schicht).
    // Drag aus Legende ohne assignmentId → kein delete möglich.
    if (dropTargetTrash && dragShift.assignmentId) {
      deleteShiftMut.mutate(dragShift.assignmentId)
      cancelDrag()
      return
    }
    if (!dropTarget) {
      cancelDrag()
      return
    }

    const { assignmentId, shiftTypeId, sourceUserId, sourceDate } = dragShift
    const { userId: targetUserId, date: targetDate } = dropTarget

    // No-op: dropped on same cell
    if (sourceUserId === targetUserId && sourceDate === targetDate) {
      cancelDrag()
      return
    }

    if (assignmentId && sourceUserId === targetUserId && sourceDate !== targetDate) {
      // Same user, different day → MOVE
      moveShiftMut.mutate({ id: assignmentId, date: targetDate })
    } else {
      // Different user or legend drag → COPY / create
      copyShiftMut.mutate({
        userId: targetUserId,
        date: targetDate,
        shiftTypeId,
      })
    }

    cancelDrag()
  }, [
    dragShift,
    dropTarget,
    dropTargetTrash,
    cancelDrag,
    moveShiftMut,
    copyShiftMut,
    deleteShiftMut,
  ])

  // Global pointermove/pointerup during drag
  useEffect(() => {
    if (!dragShift) return

    const onMove = (e: PointerEvent) => {
      setGhostPos({ x: e.clientX, y: e.clientY })
    }
    const onUp = (e: PointerEvent) => {
      if (pointerIdRef.current !== null && e.pointerId !== pointerIdRef.current) return
      handleDrop()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelDrag()
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', cancelDrag)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', cancelDrag)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [dragShift, handleDrop, cancelDrag])

  const startDragFromBadge = useCallback((e: React.PointerEvent, assignment: ShiftAssignment) => {
    e.preventDefault()
    e.stopPropagation()
    isDragging.current = true
    pointerIdRef.current = e.pointerId
    ;(e.target as HTMLElement).releasePointerCapture?.(e.pointerId)
    setDragShift({
      assignmentId: assignment.id,
      shiftTypeId: assignment.shiftTypeId ?? '',
      sourceUserId: assignment.userId,
      sourceDate: assignment.date,
      shiftTypeName: assignment.shiftTypeName ?? '',
      shiftTypeShortName: assignment.shiftTypeShortName ?? 'C',
      shiftTypeColor: assignment.shiftTypeColor ?? '#6B7280',
    })
    setGhostPos({ x: e.clientX, y: e.clientY })
  }, [])

  const startDragFromLegend = useCallback((e: React.PointerEvent, st: ShiftType) => {
    e.preventDefault()
    isDragging.current = true
    pointerIdRef.current = e.pointerId
    ;(e.target as HTMLElement).releasePointerCapture?.(e.pointerId)
    setDragShift({
      assignmentId: null,
      shiftTypeId: st.id,
      sourceUserId: null,
      sourceDate: null,
      shiftTypeName: st.name,
      shiftTypeShortName: st.shortName,
      shiftTypeColor: st.color,
    })
    setGhostPos({ x: e.clientX, y: e.clientY })
  }, [])

  const handleBadgePointerDown = useCallback(
    (e: React.PointerEvent, assignment: ShiftAssignment) => {
      if (!isAdmin) return
      if (e.pointerType === 'touch') {
        // Touch: 400ms long-press to start drag
        dragStartPos.current = { x: e.clientX, y: e.clientY }
        pointerIdRef.current = e.pointerId
        longPressTimer.current = setTimeout(() => {
          startDragFromBadge(e, assignment)
        }, 400)
      } else {
        // Mouse: start after 4px movement threshold
        dragStartPos.current = { x: e.clientX, y: e.clientY }
        pointerIdRef.current = e.pointerId
        const onMouseMove = (me: PointerEvent) => {
          if (!dragStartPos.current) return
          const dx = me.clientX - dragStartPos.current.x
          const dy = me.clientY - dragStartPos.current.y
          if (Math.sqrt(dx * dx + dy * dy) > 4) {
            window.removeEventListener('pointermove', onMouseMove)
            window.removeEventListener('pointerup', onMouseUp)
            isDragging.current = true
            pointerIdRef.current = me.pointerId
            setDragShift({
              assignmentId: assignment.id,
              shiftTypeId: assignment.shiftTypeId ?? '',
              sourceUserId: assignment.userId,
              sourceDate: assignment.date,
              shiftTypeName: assignment.shiftTypeName ?? '',
              shiftTypeShortName: assignment.shiftTypeShortName ?? 'C',
              shiftTypeColor: assignment.shiftTypeColor ?? '#6B7280',
            })
            setGhostPos({ x: me.clientX, y: me.clientY })
          }
        }
        const onMouseUp = () => {
          window.removeEventListener('pointermove', onMouseMove)
          window.removeEventListener('pointerup', onMouseUp)
          dragStartPos.current = null
        }
        window.addEventListener('pointermove', onMouseMove)
        window.addEventListener('pointerup', onMouseUp)
      }
    },
    [isAdmin, startDragFromBadge],
  )

  const handleBadgePointerMove = useCallback((e: React.PointerEvent) => {
    // Cancel long-press if finger moved >10px
    if (longPressTimer.current && dragStartPos.current) {
      const dx = e.clientX - dragStartPos.current.x
      const dy = e.clientY - dragStartPos.current.y
      if (Math.sqrt(dx * dx + dy * dy) > 10) {
        clearTimeout(longPressTimer.current)
        longPressTimer.current = null
      }
    }
  }, [])

  const handleBadgePointerUp = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  if (meLoading) return null
  if (!me?.user) return <Navigate to="/login" />

  // Navigation
  const onPrev = () => {
    if (view === 'week') setCursor((d) => addDays(d, -7))
    else setCursor((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))
  }
  const onNext = () => {
    if (view === 'week') setCursor((d) => addDays(d, 7))
    else setCursor((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))
  }
  const onToday = () => {
    if (view === 'week') setCursor(startOfWeek(new Date()))
    else setCursor(startOfMonth(new Date()))
  }

  // Kanonisches Format (lib/period-title): „KW 28 · 06.–12. Juli 2026" —
  // identisch zu Kalender-Woche/Disposition.
  const viewTitle =
    view === 'week'
      ? periodTitle(days[0], days[days.length - 1])
      : `${MONTHS_LONG[cursor.getMonth()]} ${cursor.getFullYear()}`

  const todayStr = formatDateISO(new Date())
  const isCompact = view === 'month'

  return (
    <section className="animate-fade-in space-y-4">
      {/* Mobile-Shell (<640): ◀ Zeitraum ▶ in der Top-Mitte — Kalender-
          Analogie; Tap auf den Titel springt zu Heute (Titel●-Idiom der
          mobilen Kalender-Leiste, hier ohne Status-Dot). */}
      <MobileTopCenter>
        <button
          type="button"
          onClick={onPrev}
          aria-label="Vorheriger Zeitraum"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-text-secondary hover:bg-bg-overlay hover:text-text-primary"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={onToday}
          aria-label={`Zeitraum: ${viewTitle} — zu Heute springen`}
          // Inline minWidth:0 schlägt die globale .no-min-tap-Regel, sonst
          // sprengt der lange Wochentitel die Leiste (Gotcha der alten
          // Kalender-Leiste); h-11 hält die 44px-Tap-Höhe.
          style={{ minWidth: 0 }}
          className="no-min-tap flex h-11 min-w-0 flex-1 items-center justify-center rounded-md px-1 text-text-primary hover:bg-bg-overlay"
        >
          <span className="truncate text-xs font-medium">{viewTitle}</span>
        </button>
        <button
          type="button"
          onClick={onNext}
          aria-label="Nächster Zeitraum"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-text-secondary hover:bg-bg-overlay hover:text-text-primary"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </MobileTopCenter>

      {/* Bottom-Slots (Phone + Tablet-Band): Ansicht (Woche/Monat) · Team-Filter · Mehr (Admin-
          Aktionen gebündelt) — jeweils als Sheet, bestehende Handler. */}
      <MobileBottomSlots>
        <BarSlot
          label="Ansicht"
          icon={<CalendarRange className="h-5 w-5" />}
          ariaLabel="Ansicht wählen (Woche oder Monat)"
          ariaHasPopup
          onClick={() => setMobileSheet('view')}
        />
        {teams.length > 0 && (
          <BarSlot
            label="Team"
            icon={<UsersRound className="h-5 w-5" />}
            active={teamFilter !== ''}
            ariaLabel="Nach Team filtern"
            ariaHasPopup
            onClick={() => setMobileSheet('team')}
          />
        )}
        {isAdmin && (
          <BarSlot
            label="Mehr"
            icon={<MoreHorizontal className="h-5 w-5" />}
            ariaLabel="Weitere Aktionen"
            ariaHasPopup
            onClick={() => setMobileSheet('more')}
          />
        )}
      </MobileBottomSlots>

      {/* Ansicht-Sheet */}
      <SidePanel open={mobileSheet === 'view'} onClose={() => setMobileSheet(null)} title="Ansicht">
        <div className="flex flex-col gap-1">
          <SheetRow active={view === 'week'} label="Woche" onClick={() => setView('week')} />
          <SheetRow active={view === 'month'} label="Monat" onClick={() => setView('month')} />
        </div>
      </SidePanel>

      {/* Team-Filter-Sheet */}
      <SidePanel
        open={mobileSheet === 'team'}
        onClose={() => setMobileSheet(null)}
        title="Team-Filter"
      >
        <div className="flex flex-col gap-1">
          <SheetRow
            active={teamFilter === ''}
            label="Alle Teams"
            onClick={() => setTeamFilter('')}
          />
          {teams.map((t) => (
            <SheetRow
              key={t.id}
              active={teamFilter === t.id}
              label={t.name}
              onClick={() => setTeamFilter(t.id)}
            />
          ))}
        </div>
      </SidePanel>

      {/* Mehr-Sheet (Admin): bündelt die Kopf-Aktionen. */}
      <SidePanel
        open={mobileSheet === 'more'}
        onClose={() => setMobileSheet(null)}
        title="Aktionen"
      >
        <div className="flex flex-col gap-1">
          {view === 'week' && (
            <SheetRow
              active={false}
              label="Vorwoche kopieren"
              onClick={() => {
                setMobileSheet(null)
                copyWeekMut.mutate()
              }}
            />
          )}
          <SheetRow
            active={false}
            label="Schichttypen verwalten"
            onClick={() => {
              setMobileSheet(null)
              setTypesOpen(true)
            }}
          />
        </div>
      </SidePanel>

      {/* In-Content-Kopf: <640 übernimmt die Shell (Titel oben, Aktionen
          unten) — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <PageHeader
        title="Teamplanung"
        actions={
          isAdmin && (
            <>
              <Button variant="outline" size="sm" onClick={() => setTypesOpen(true)}>
                <Settings2 className="h-4 w-4" /> Schichttypen
              </Button>
              {view === 'week' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyWeekMut.mutate()}
                  disabled={copyWeekMut.isPending}
                >
                  <Copy className="h-4 w-4" /> Vorwoche kopieren
                </Button>
              )}
            </>
          )
        }
      />

      {/* Navigation + view switcher — <640 sitzt der Zeitraum oben, Ansicht/
          Team in den Bottom-Sheets (phone:hidden). */}
      <div className="flex phone:hidden items-center gap-2 flex-wrap">
        <Button variant="ghost" size="icon" onClick={onPrev}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="secondary" size="sm" onClick={onToday}>
          Heute
        </Button>
        <Button variant="ghost" size="icon" onClick={onNext}>
          <ChevronRight className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium text-text-primary ml-2">{viewTitle}</span>
        <span className="flex-1" />
        {teams.length > 0 && (
          <Select
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            className="w-40 h-8 text-xs"
          >
            <option value="">Alle Teams</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        )}
        <SegmentedControl value={view} onChange={setView} options={VIEW_OPTIONS} />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 text-xs">
        {(['vacation', 'sick', 'training'] as AbsenceType[]).map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 border border-border-subtle"
            style={{ backgroundColor: ABSENCE_BG[t] }}
          >
            <span className="text-[11px]">{ABSENCE_ICON[t]}</span> {ABSENCE_LABEL[t]}
          </span>
        ))}
        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 border border-red-400/40 bg-red-500/10 text-red-400 text-2xs">
          <AlertTriangle className="h-3 w-3" /> Konflikt
        </span>
        {/* Draggable shift type quick-assign badges (admin only) */}
        {isAdmin &&
          types.map((st) => (
            <span
              key={st.id}
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 border border-border-subtle cursor-grab select-none touch-none active:cursor-grabbing"
              style={{ backgroundColor: `${st.color}20` }}
              onPointerDown={(e) => startDragFromLegend(e, st)}
            >
              <span
                className="inline-flex h-4 w-4 items-center justify-center rounded-sm text-2xs leading-none font-bold text-white"
                style={{ backgroundColor: st.color }}
              >
                {st.shortName.charAt(0)}
              </span>
              <span className="text-text-secondary">{st.shortName}</span>
            </span>
          ))}
      </div>

      {/* Grid */}
      {isLoading ? (
        <p className="text-sm text-text-tertiary py-8 text-center">Lade Teamplanung …</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border-subtle bg-bg-elevated/40">
          <table
            className="w-full border-collapse text-xs"
            style={{ minWidth: isCompact ? '900px' : '700px' }}
          >
            <thead>
              <tr>
                <th className="text-left p-2 border-b border-border-subtle text-text-secondary font-medium sticky left-0 bg-bg-elevated/90 backdrop-blur z-10 min-w-[140px]">
                  Mitarbeiter
                </th>
                {days.map((d) => {
                  const dateStr = formatDateISO(d)
                  const isToday = dateStr === todayStr
                  const wd = d.getDay()
                  const isWeekend = wd === 0 || wd === 6
                  const dayIdx = (d.getDay() + 6) % 7 // Monday=0 ... Sunday=6
                  return (
                    <th
                      key={dateStr}
                      className={cn(
                        'border-b border-border-subtle text-text-secondary font-medium text-center',
                        isCompact ? 'p-0.5 min-w-[28px]' : 'p-2 min-w-[100px]',
                        isWeekend && 'bg-bg-overlay/30',
                        isToday && 'bg-accent/8',
                      )}
                    >
                      <div className={cn('text-2xs uppercase', isToday && 'text-accent')}>
                        {isCompact ? WEEKDAY_FROM_JS[wd]?.charAt(0) : WEEKDAYS[dayIdx]}
                      </div>
                      <div
                        className={cn(
                          isCompact ? 'text-2xs' : 'text-sm',
                          isToday && 'text-accent font-semibold',
                        )}
                      >
                        {d.getDate()}
                      </div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr
                  key={user.id}
                  className="border-b border-border-subtle/50 hover:bg-bg-elevated/30"
                >
                  <td className="p-2 text-text-primary font-medium truncate max-w-[160px] sticky left-0 bg-bg-elevated/90 backdrop-blur z-10 border-r border-border-subtle/50">
                    <div className="flex items-center gap-2">
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent text-2xs font-semibold">
                        {user.name.slice(0, 2).toUpperCase()}
                      </div>
                      <span className="truncate">{user.name}</span>
                    </div>
                  </td>
                  {days.map((d) => {
                    const dateStr = formatDateISO(d)
                    const cellKey = `${user.id}_${dateStr}`
                    const cellAssignments = assignmentMap[cellKey] ?? []
                    const cellAbsences = absenceMap[cellKey] ?? []
                    const isToday = dateStr === todayStr
                    const wd = d.getDay()
                    const isWeekend = wd === 0 || wd === 6
                    const hasConflict = cellAssignments.length > 0 && cellAbsences.length > 0

                    // Determine background from absence type (first one wins)
                    const absenceBg =
                      cellAbsences.length > 0 ? ABSENCE_BG[cellAbsences[0].type] : undefined

                    const isDropTarget =
                      dragShift && dropTarget?.userId === user.id && dropTarget?.date === dateStr

                    return (
                      // biome-ignore lint/a11y/useKeyWithClickEvents: planning grid cell
                      <td
                        key={dateStr}
                        className={cn(
                          'text-center transition-colors relative',
                          isCompact ? 'p-0.5' : 'p-1',
                          isWeekend && 'bg-bg-overlay/20',
                          isToday && 'bg-accent/5',
                          isAdmin &&
                            !dragShift &&
                            'cursor-pointer hover:bg-bg-elevated/60 touch:opacity-100',
                          isDropTarget && 'ring-2 ring-accent/60 ring-inset bg-accent/10',
                        )}
                        style={{
                          ...(absenceBg && !isDropTarget ? { backgroundColor: absenceBg } : {}),
                          ...(dragShift ? { touchAction: 'none' } : {}),
                        }}
                        onClick={
                          isAdmin && !isDragging.current
                            ? () =>
                                setEditCell({
                                  userId: user.id,
                                  date: dateStr,
                                  userName: user.name,
                                })
                            : undefined
                        }
                        onPointerEnter={
                          dragShift
                            ? () => setDropTarget({ userId: user.id, date: dateStr })
                            : undefined
                        }
                        onPointerLeave={
                          dragShift
                            ? () =>
                                setDropTarget((prev) =>
                                  prev?.userId === user.id && prev?.date === dateStr ? null : prev,
                                )
                            : undefined
                        }
                      >
                        {/* Conflict warning */}
                        {hasConflict && (
                          <span
                            className="absolute top-0 right-0 text-red-400"
                            title="Schicht + Abwesenheit am gleichen Tag"
                          >
                            <AlertTriangle className="h-3 w-3" />
                          </span>
                        )}

                        {/* Content */}
                        {isCompact ? (
                          <CompactCell
                            assignments={cellAssignments}
                            absences={cellAbsences}
                            isAdmin={!!isAdmin}
                            onBadgePointerDown={handleBadgePointerDown}
                            onBadgePointerMove={handleBadgePointerMove}
                            onBadgePointerUp={handleBadgePointerUp}
                          />
                        ) : (
                          <DetailCell
                            assignments={cellAssignments}
                            absences={cellAbsences}
                            isAdmin={!!isAdmin}
                            onBadgePointerDown={handleBadgePointerDown}
                            onBadgePointerMove={handleBadgePointerMove}
                            onBadgePointerUp={handleBadgePointerUp}
                          />
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}

              {/* Empty state */}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={days.length + 1} className="text-center py-8 text-text-tertiary">
                    <UsersRound
                      className="h-10 w-10 mx-auto mb-2 text-text-tertiary/40"
                      strokeWidth={1.5}
                    />
                    Keine Mitarbeiter gefunden.
                  </td>
                </tr>
              )}

              {/* Summary row */}
              {filteredUsers.length > 0 && (
                <tr className="border-t-2 border-border-subtle bg-bg-elevated/60">
                  <td className="p-2 text-text-secondary font-medium text-2xs uppercase tracking-wider sticky left-0 bg-bg-elevated/90 backdrop-blur z-10">
                    Zusammenfassung
                  </td>
                  {days.map((d) => {
                    const dateStr = formatDateISO(d)
                    const counts = summaryByDay[dateStr] ?? {}
                    const entries = Object.entries(counts)
                    const isWeekend = d.getDay() === 0 || d.getDay() === 6
                    return (
                      <td
                        key={dateStr}
                        className={cn('text-center p-1', isWeekend && 'bg-bg-overlay/20')}
                      >
                        {entries.length === 0 ? (
                          <span className="text-text-tertiary/30">&ndash;</span>
                        ) : (
                          <div
                            className={cn(
                              'flex flex-col gap-0.5 items-center',
                              isCompact && 'gap-0',
                            )}
                          >
                            {entries.map(([shortName, count]) => (
                              <span
                                key={shortName}
                                className="text-2xs text-text-secondary font-mono"
                              >
                                {shortName}:{count}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                    )
                  })}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-text-tertiary">
        {isAdmin
          ? 'Als Admin kannst du Schichten zuweisen. Abwesenheiten werden über den Kalender erstellt.'
          : 'Schichten werden vom Admin zugewiesen. Abwesenheiten erstellst du über den Kalender.'}
      </p>

      {/* Edit cell panel (admin only) */}
      {editCell && isAdmin && (
        <ShiftCellPanel
          userId={editCell.userId}
          date={editCell.date}
          userName={editCell.userName}
          types={types}
          existingAssignments={assignmentMap[`${editCell.userId}_${editCell.date}`] ?? []}
          cellAbsences={absenceMap[`${editCell.userId}_${editCell.date}`] ?? []}
          onClose={() => setEditCell(null)}
          onChanged={() => {
            qc.invalidateQueries({ queryKey: ['shifts'] })
          }}
        />
      )}

      {/* Shift types management */}
      {typesOpen && isAdmin && (
        <ShiftTypesPanel
          types={types}
          onClose={() => setTypesOpen(false)}
          onChanged={() => qc.invalidateQueries({ queryKey: ['shift-types'] })}
        />
      )}

      {/* Drag ghost */}
      {dragShift && ghostPos && (
        <div
          className="pointer-events-none fixed z-50"
          style={{
            left: ghostPos.x - 16,
            top: ghostPos.y - 14,
          }}
        >
          <span
            className="inline-flex items-center justify-center px-2.5 py-1 rounded text-[11px] font-bold text-white shadow-lg opacity-85"
            style={{ backgroundColor: dragShift.shiftTypeColor }}
          >
            {dragShift.shiftTypeShortName}
          </span>
        </div>
      )}

      {/* Mülleimer-Drop-Zone — sichtbar nur während eines aktiven Drags einer
          bestehenden Assignment. Bei Hover/Pointer-Over markiert sich rot
          und löscht die Schicht beim Loslassen. */}
      {dragShift?.assignmentId && (
        <button
          type="button"
          onPointerEnter={() => setDropTargetTrash(true)}
          onPointerLeave={() => setDropTargetTrash(false)}
          // Click ignored — Drop-Target via pointerEnter. Aber type=button
          // sodass kein Submit eines umgebenden form passiert.
          className={`fixed bottom-6 right-6 z-50 h-16 w-16 rounded-full shadow-2xl flex items-center justify-center transition-all duration-150 ${
            dropTargetTrash
              ? 'bg-red-600 scale-125 ring-4 ring-red-600/40'
              : 'bg-red-600 hover:bg-red-700'
          }`}
          aria-label="Schicht löschen — hier loslassen"
        >
          <Trash2 className="h-7 w-7 text-white" />
        </button>
      )}
    </section>
  )
}

// ── Compact cell (month view) ───────────────────────────────────────────

function CompactCell({
  assignments,
  absences,
  isAdmin,
  onBadgePointerDown,
  onBadgePointerMove,
  onBadgePointerUp,
}: {
  assignments: ShiftAssignment[]
  absences: Absence[]
  isAdmin: boolean
  onBadgePointerDown?: (e: React.PointerEvent, a: ShiftAssignment) => void
  onBadgePointerMove?: (e: React.PointerEvent) => void
  onBadgePointerUp?: () => void
}) {
  if (assignments.length === 0 && absences.length === 0) {
    return isAdmin ? <span className="text-text-tertiary/30 text-sm leading-none">+</span> : null
  }

  return (
    <div className="flex flex-col items-center gap-0.5 min-h-[20px] justify-center">
      {assignments.map((a) => (
        <span
          key={a.id}
          className={cn(
            'inline-block h-3 w-3 rounded-sm text-2xs font-bold text-white leading-[12px]',
            isAdmin && 'cursor-grab active:cursor-grabbing',
          )}
          style={{
            backgroundColor: a.shiftTypeColor ?? '#6B7280',
            touchAction: isAdmin ? 'none' : undefined,
          }}
          title={a.shiftTypeName ?? a.customStart ?? ''}
          onPointerDown={isAdmin ? (e) => onBadgePointerDown?.(e, a) : undefined}
          onPointerMove={isAdmin ? onBadgePointerMove : undefined}
          onPointerUp={isAdmin ? onBadgePointerUp : undefined}
        >
          {(a.shiftTypeShortName ?? 'C').charAt(0)}
        </span>
      ))}
      {absences.length > 0 && assignments.length === 0 && (
        <span className="text-2xs leading-none" title={ABSENCE_LABEL[absences[0].type]}>
          {ABSENCE_ICON[absences[0].type]}
        </span>
      )}
    </div>
  )
}

// ── Detail cell (week view) ─────────────────────────────────────────────

function DetailCell({
  assignments,
  absences,
  isAdmin,
  onBadgePointerDown,
  onBadgePointerMove,
  onBadgePointerUp,
}: {
  assignments: ShiftAssignment[]
  absences: Absence[]
  isAdmin: boolean
  onBadgePointerDown?: (e: React.PointerEvent, a: ShiftAssignment) => void
  onBadgePointerMove?: (e: React.PointerEvent) => void
  onBadgePointerUp?: () => void
}) {
  if (assignments.length === 0 && absences.length === 0) {
    return isAdmin ? <span className="text-text-tertiary/30 text-lg leading-none">+</span> : null
  }

  return (
    <div className="flex flex-col gap-0.5 items-center min-h-[32px] justify-center">
      {assignments.map((a) => (
        <span
          key={a.id}
          className={cn(
            'inline-flex items-center justify-center px-2 py-1 rounded text-2xs font-bold text-white leading-none',
            isAdmin && 'cursor-grab active:cursor-grabbing',
          )}
          style={{
            backgroundColor: a.shiftTypeColor ?? '#6B7280',
            touchAction: isAdmin ? 'none' : undefined,
          }}
          title={`${a.shiftTypeName ?? ''} ${a.customStart ? `${a.customStart}–${a.customEnd}` : ''}`}
          onPointerDown={isAdmin ? (e) => onBadgePointerDown?.(e, a) : undefined}
          onPointerMove={isAdmin ? onBadgePointerMove : undefined}
          onPointerUp={isAdmin ? onBadgePointerUp : undefined}
        >
          {a.shiftTypeShortName ?? 'C'}
        </span>
      ))}
      {absences.length > 0 && (
        <span
          className="text-2xs leading-none"
          title={absences.map((a) => ABSENCE_LABEL[a.type]).join(', ')}
        >
          {ABSENCE_ICON[absences[0].type]}{' '}
          <span className="text-2xs text-text-secondary">{ABSENCE_LABEL[absences[0].type]}</span>
        </span>
      )}
    </div>
  )
}

// ── Shift Cell Panel ────────────────────────────────────────────────────

function ShiftCellPanel({
  userId,
  date,
  userName,
  types,
  existingAssignments,
  cellAbsences,
  onClose,
  onChanged,
}: {
  userId: string
  date: string
  userName: string
  types: ShiftType[]
  existingAssignments: ShiftAssignment[]
  cellAbsences: Absence[]
  onClose: () => void
  onChanged: () => void
}) {
  const [selectedTypeId, setSelectedTypeId] = useState('')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')

  const createMut = useMutation({
    mutationFn: () =>
      api.createShift({
        userId,
        date,
        shiftTypeId: selectedTypeId || null,
        customStart: customStart || null,
        customEnd: customEnd || null,
      }),
    onSuccess: () => {
      onChanged()
      notify.success('Schicht zugewiesen')
      setSelectedTypeId('')
      setCustomStart('')
      setCustomEnd('')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteShift(id),
    onSuccess: () => {
      onChanged()
      notify.success('Schicht entfernt')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const dateLabel = new Date(`${date}T00:00`).toLocaleDateString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  })

  return (
    <SidePanel
      open
      onClose={onClose}
      title={userName}
      description={dateLabel}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Schließen
        </Button>
      }
    >
      <div className="space-y-4">
        {/* Absence info (read-only) */}
        {cellAbsences.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">
              Abwesenheit (nur lesbar)
            </p>
            {cellAbsences.map((a) => (
              <div
                key={a.id}
                className="flex items-center gap-2 p-2 rounded-lg border border-border-subtle"
                style={{ backgroundColor: ABSENCE_BG[a.type] }}
              >
                <span className="text-sm">{ABSENCE_ICON[a.type]}</span>
                <span className="text-sm text-text-primary flex-1">
                  {ABSENCE_LABEL[a.type]}
                  {a.notes && (
                    <span className="text-text-tertiary ml-1 text-xs">&middot; {a.notes}</span>
                  )}
                </span>
                <span className="text-2xs text-text-tertiary">
                  {new Date(a.fromDate).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: 'short',
                  })}{' '}
                  &ndash;{' '}
                  {new Date(a.toDate).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: 'short',
                  })}
                </span>
              </div>
            ))}
            {existingAssignments.length > 0 && (
              <div className="flex items-center gap-1.5 p-2 rounded-lg border border-red-400/40 bg-red-500/10 text-red-400 text-xs">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                Konflikt: Schicht und Abwesenheit am gleichen Tag
              </div>
            )}
          </div>
        )}

        {/* Existing shift assignments */}
        {existingAssignments.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">
              Zugewiesene Schichten
            </p>
            {existingAssignments.map((a) => (
              <div
                key={a.id}
                className="flex items-center gap-2 p-2 rounded-lg border border-border-subtle bg-bg-overlay/30"
              >
                <span
                  className="h-6 w-6 rounded flex items-center justify-center text-2xs font-bold text-white"
                  style={{ backgroundColor: a.shiftTypeColor ?? '#6B7280' }}
                >
                  {a.shiftTypeShortName ?? 'C'}
                </span>
                <span className="text-sm text-text-primary flex-1">
                  {a.shiftTypeName ?? `${a.customStart} – ${a.customEnd}`}
                </span>
                <button
                  type="button"
                  onClick={() => deleteMut.mutate(a.id)}
                  className="p-1 text-text-tertiary hover:text-red-400 touch:opacity-100"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add new shift */}
        <div className="space-y-3 border-t border-border-subtle pt-3">
          <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">
            Schicht hinzufügen
          </p>
          <div className="space-y-2">
            <Label className="text-xs">Schichttyp</Label>
            <Select
              value={selectedTypeId}
              onChange={(e) => setSelectedTypeId(e.target.value)}
              className="h-9 text-sm"
            >
              <option value="">Benutzerdefiniert</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.shortName} – {t.name} ({t.startTime}–{t.endTime})
                </option>
              ))}
            </Select>
          </div>
          {!selectedTypeId && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Von</Label>
                <Input
                  type="time"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Bis</Label>
                <Input
                  type="time"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
          )}
          <Button
            size="sm"
            disabled={createMut.isPending || (!selectedTypeId && (!customStart || !customEnd))}
            onClick={() => createMut.mutate()}
            className="w-full"
          >
            <Plus className="h-4 w-4" /> Zuweisen
          </Button>
        </div>
      </div>
    </SidePanel>
  )
}

// ── Shift Types Management Panel ────────────────────────────────────────

function ShiftTypesPanel({
  types,
  onClose,
  onChanged,
}: {
  types: ShiftType[]
  onClose: () => void
  onChanged: () => void
}) {
  const [newName, setNewName] = useState('')
  const [newShort, setNewShort] = useState('')
  const [newColor, setNewColor] = useState('#3B82F6')
  const [newStart, setNewStart] = useState('08:00')
  const [newEnd, setNewEnd] = useState('16:00')
  const [newBreak, setNewBreak] = useState(30)

  const createMut = useMutation({
    mutationFn: () =>
      api.createShiftType({
        name: newName,
        shortName: newShort,
        color: newColor,
        startTime: newStart,
        endTime: newEnd,
        breakMinutes: newBreak,
      }),
    onSuccess: () => {
      onChanged()
      notify.success('Schichttyp erstellt')
      setNewName('')
      setNewShort('')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteShiftType(id),
    onSuccess: () => {
      onChanged()
      notify.success('Schichttyp gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Schichttypen verwalten"
      footer={
        <Button variant="ghost" onClick={onClose}>
          Schließen
        </Button>
      }
    >
      <div className="space-y-4">
        {types.map((t) => (
          <div
            key={t.id}
            className="flex items-center gap-2 p-2 rounded-lg border border-border-subtle"
          >
            <span
              className="h-7 w-7 rounded flex items-center justify-center text-xs font-bold text-white shrink-0"
              style={{ backgroundColor: t.color }}
            >
              {t.shortName}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary truncate">{t.name}</p>
              <p className="text-2xs text-text-tertiary">
                {t.startTime}–{t.endTime} &middot; {t.breakMinutes} min Pause
              </p>
            </div>
            <button
              type="button"
              onClick={() => deleteMut.mutate(t.id)}
              className="p-1 text-text-tertiary hover:text-red-400 touch:opacity-100"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}

        <div className="border-t border-border-subtle pt-3 space-y-3">
          <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">
            Neuer Schichttyp
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Fruehschicht"
                className="h-8"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Kürzel</Label>
              <Input
                value={newShort}
                onChange={(e) => setNewShort(e.target.value.slice(0, 5))}
                placeholder="F"
                className="h-8"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Von</Label>
              <Input
                type="time"
                value={newStart}
                onChange={(e) => setNewStart(e.target.value)}
                className="h-8"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Bis</Label>
              <Input
                type="time"
                value={newEnd}
                onChange={(e) => setNewEnd(e.target.value)}
                className="h-8"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Pause (min)</Label>
              <Input
                type="number"
                value={newBreak}
                onChange={(e) => setNewBreak(Number(e.target.value))}
                className="h-8"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Farbe</Label>
            <input
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              className="h-7 w-7 rounded border border-border-default cursor-pointer"
            />
          </div>
          <Button
            size="sm"
            disabled={!newName.trim() || !newShort.trim() || createMut.isPending}
            onClick={() => createMut.mutate()}
            className="w-full"
          >
            <Plus className="h-4 w-4" /> Erstellen
          </Button>
        </div>
      </div>
    </SidePanel>
  )
}
